import {
  // ArrowLeftRight,
  Building2,
  Boxes,
  ContactRound,
  Plane,
  ReceiptText,
  ShoppingBag,
  UserCog,
  UsersRound,
  WalletCards,
  type LucideIcon,
} from 'lucide-react'

import { PERMISSIONS, type Permission } from '@/features/auth'

/**
 * =============================================================================
 * MAPA DE RUTAS
 * =============================================================================
 * Las rutas se definen aquí como constantes y NUNCA se escriben a mano en el
 * código. En vez de `<Link to="/customers">`, se escribe `<Link to={ROUTES.CUSTOMERS}>`.
 *
 * ¿Por qué? Un enlace mal escrito (`/customer` en vez de `/customers`) es un
 * bug que TypeScript no puede detectar: es solo texto. Con constantes, un typo
 * es un error de compilación. Y renombrar una ruta pasa a ser cambiar una
 * línea en vez de buscar por todo el proyecto.
 * =============================================================================
 */
export const ROUTES = {
  ROOT: '/',
  LOGIN: '/login',
  REQUIRED_PASSWORD: '/required-password',
  DASHBOARD: '/dashboard',
  CUSTOMERS: '/customers',
  CUSTOMER_DETAIL: '/customers/:customerId',
  TRIP_SALES: '/trip-sales',
  INDIVIDUAL_TRIPS: '/individual-trips',
  INDIVIDUAL_TRIPS_REGISTER: '/individual-trips/register',
  INDIVIDUAL_TRIP_DETAIL: '/individual-trips/:tripSaleId',
  TRIP_SALE_DETAIL: '/trip-sales/:tripSaleId',
  GROUP_TRIPS: '/group-trips',
  GROUP_TRIP_DETAIL: '/group-trips/:groupTripId',
  ABONOS: '/abonos',
  PAYMENTS: '/payments',
  OPERATORS: '/operators',
  OPERATOR_DETAIL: '/operators/:operatorId',
  INVENTORY: '/inventory',
  PRODUCTS: '/products',
  PRODUCT_SALES: '/product-sales',
  PRODUCT_SALE_DETAIL: '/product-sales/:productSaleId',
  PRODUCT_ABONOS: '/product-abonos',
  CASH_REGISTER: '/cash-register',
  SELLERS: '/sellers',
  USERS: '/users',
  ADMIN_USERS_SELLERS: '/administration/users-sellers',
  FORBIDDEN: '/forbidden',
} as const

export type Route = (typeof ROUTES)[keyof typeof ROUTES]

/**
 * Elementos del menú lateral.
 *
 * Cada entrada declara el permiso necesario para verla. La barra lateral se
 * construye a partir de esta lista y filtra sola según el usuario: un cajero
 * no ve "Cortesías" porque no tiene `inventory:view`.
 *
 * Ventaja de tenerlo como datos y no como JSX: agregar un módulo al menú es
 * añadir un objeto a este array. No se toca el componente de la barra lateral.
 *
 * Las etiquetas van en español: las lee el usuario.
 */
export interface MenuItem {
  label: string
  path: Route
  icon: LucideIcon
  permission: Permission
  section: MenuSection
}

export type MenuSection = 'VIAJES' | 'PRODUCTOS' | 'AGENCIA' | 'SISTEMA'

export const MENU_SECTIONS: readonly { key: MenuSection; label: MenuSection }[] = [
  { key: 'VIAJES', label: 'VIAJES' },
  { key: 'PRODUCTOS', label: 'PRODUCTOS' },
  { key: 'AGENCIA', label: 'AGENCIA' },
  { key: 'SISTEMA', label: 'SISTEMA' },
]

export const MAIN_MENU: readonly MenuItem[] = [
  {
    label: 'Individuales',
    path: ROUTES.INDIVIDUAL_TRIPS,
    icon: Plane,
    permission: PERMISSIONS.INDIVIDUAL_TRIPS_VIEW,
    section: 'VIAJES',
  },
  {
    label: 'Grupales',
    path: ROUTES.GROUP_TRIPS,
    icon: UsersRound,
    permission: PERMISSIONS.GROUP_TRIPS_VIEW,
    section: 'VIAJES',
  },
  {
    label: 'Abonos',
    path: ROUTES.ABONOS,
    icon: WalletCards,
    permission: PERMISSIONS.PAYMENTS_VIEW,
    section: 'VIAJES',
  },
  {
    label: 'Operadores',
    path: ROUTES.OPERATORS,
    icon: Building2,
    permission: PERMISSIONS.OPERATORS_VIEW,
    section: 'VIAJES',
  },
  {
    label: 'Catálogo',
    path: ROUTES.PRODUCTS,
    icon: ShoppingBag,
    permission: PERMISSIONS.PRODUCTS_VIEW,
    section: 'PRODUCTOS',
  },
  {
    label: 'Ventas',
    path: ROUTES.PRODUCT_SALES,
    icon: ReceiptText,
    permission: PERMISSIONS.PRODUCT_SALES_VIEW,
    section: 'PRODUCTOS',
  },
  {
    label: 'Abonos',
    path: ROUTES.PRODUCT_ABONOS,
    icon: WalletCards,
    permission: PERMISSIONS.PRODUCT_SALES_VIEW,
    section: 'PRODUCTOS',
  },
  {
    label: 'Clientes',
    path: ROUTES.CUSTOMERS,
    icon: ContactRound,
    permission: PERMISSIONS.CUSTOMERS_VIEW,
    section: 'AGENCIA',
  },
  {
    label: 'Inventario',
    path: ROUTES.INVENTORY,
    icon: Boxes,
    permission: PERMISSIONS.INVENTORY_VIEW,
    section: 'AGENCIA',
  },
  // {
  //   label: 'Caja',
  //   path: ROUTES.CASH_REGISTER,
  //   icon: ArrowLeftRight,
  //   permission: PERMISSIONS.CASH_REGISTER_VIEW,
  // },
  {
    label: 'Vendedores',
    path: ROUTES.SELLERS,
    icon: ContactRound,
    permission: PERMISSIONS.USERS_MANAGE,
    section: 'AGENCIA',
  },
  {
    label: 'Usuarios',
    path: ROUTES.USERS,
    icon: UserCog,
    permission: PERMISSIONS.USERS_MANAGE,
    section: 'SISTEMA',
  },
]
